import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Upload,
  FileText,
  X,
  AlertTriangle,
  CheckCircle,
  File,
  FileImage,
  FileSpreadsheet,
  Loader2,
  Plus
} from 'lucide-react';
import { validateFileType, validateFileSize, formatFileSize, STORAGE_CONFIG } from '../../../lib/materials-r2';

/**
 * MaterialUpload - Drag-and-drop file upload component
 * 
 * Provides intuitive file upload interface with drag-and-drop support,
 * file validation, progress tracking, and bulk operations.
 * 
 * Features:
 * - Drag-and-drop file upload
 * - File type and size validation
 * - Upload progress tracking
 * - Batch file operations
 * - Version notes and metadata
 * - Preview before upload
 */
const MaterialUpload = ({ session, onUpload }) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [versionNotes, setVersionNotes] = useState('');
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
    // Reset input to allow selecting the same files again
    e.target.value = '';
  }, []);

  // Add files with validation
  const addFiles = useCallback((newFiles) => {
    const validFiles = [];
    const newErrors = [];

    newFiles.forEach(file => {
      // Check file type
      if (!validateFileType(file.type)) {
        newErrors.push({
          file: file.name,
          message: `Unsupported file type: ${file.type}`
        });
        return;
      }

      // Check file size
      if (!validateFileSize(file.size)) {
        newErrors.push({
          file: file.name,
          message: `File too large: ${formatFileSize(file.size)} (max ${formatFileSize(STORAGE_CONFIG.MAX_FILE_SIZE)})`
        });
        return;
      }

      // Check for duplicates
      const isDuplicate = files.some(existingFile => 
        existingFile.name === file.name && existingFile.size === file.size
      );

      if (isDuplicate) {
        newErrors.push({
          file: file.name,
          message: 'File already selected'
        });
        return;
      }

      validFiles.push({
        file,
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        displayName: file.name.replace(/\.[^/.]+$/, ""), // Remove extension for display name
        status: 'ready'
      });
    });

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }

    if (newErrors.length > 0) {
      setErrors(prev => [...prev, ...newErrors]);
      // Clear errors after 5 seconds
      setTimeout(() => {
        setErrors(prev => prev.filter(error => !newErrors.includes(error)));
      }, 5000);
    }
  }, [files]);

  // Remove file from list
  const removeFile = useCallback((fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const { [fileId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Update file display name
  const updateDisplayName = useCallback((fileId, displayName) => {
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, displayName } : f
    ));
  }, []);

  // Get file icon based on type
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return FileImage;
    if (type.includes('spreadsheet') || type.includes('excel')) return FileSpreadsheet;
    if (type.includes('presentation') || type.includes('powerpoint')) return FileText;
    return File;
  };

  // Start upload process
  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    
    try {
      // Initialize progress for all files
      const initialProgress = {};
      files.forEach(file => {
        initialProgress[file.id] = 0;
      });
      setUploadProgress(initialProgress);

      // Simulate upload process (replace with actual R2 upload)
      for (const file of files) {
        // Update file status
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'uploading' } : f
        ));

        // Simulate progress updates
        for (let progress = 0; progress <= 100; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          setUploadProgress(prev => ({
            ...prev,
            [file.id]: progress
          }));
        }

        // Mark as complete
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'completed' } : f
        ));
      }

      // Call parent upload handler
      const uploadData = {
        files: files.map(f => ({
          file: f.file,
          displayName: f.displayName,
          originalName: f.name
        })),
        versionNotes,
        sessionId: session.id,
        programId: session.programId
      };

      await onUpload(uploadData);

      // Reset form
      setFiles([]);
      setVersionNotes('');
      setUploadProgress({});

    } catch (error) {
      console.error('Upload failed:', error);
      setErrors(prev => [...prev, {
        file: 'Upload',
        message: 'Upload failed. Please try again.'
      }]);
      
      // Mark files as failed
      setFiles(prev => prev.map(f => ({ ...f, status: 'error' })));
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploading':
        return 'border-blue-200 bg-blue-50';
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <Card>
        <CardHeader>
          <CardTitle>Upload New Materials</CardTitle>
          <CardDescription>
            Upload files to create a new version for {session.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Drag and Drop Zone */}
          <div
            className={`
              relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragging 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
              }
              ${isUploading ? 'pointer-events-none opacity-50' : ''}
            `}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={STORAGE_CONFIG.ALLOWED_MIME_TYPES.join(',')}
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="flex flex-col items-center space-y-4">
              <div className={`
                p-3 rounded-full 
                ${isDragging ? 'bg-blue-100' : 'bg-gray-100'}
              `}>
                <Upload className={`
                  h-8 w-8 
                  ${isDragging ? 'text-blue-600' : 'text-gray-600'}
                `} />
              </div>
              
              <div>
                <p className="text-lg font-medium text-gray-900">
                  {isDragging ? 'Drop files here' : 'Drag & drop files here'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  or click to browse files
                </p>
              </div>

              <div className="text-xs text-gray-400 space-y-1">
                <p>Supported formats: PDF, PowerPoint, Word, Excel, Images</p>
                <p>Maximum file size: {formatFileSize(STORAGE_CONFIG.MAX_FILE_SIZE)}</p>
              </div>
            </div>
          </div>

          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="mt-4 space-y-2">
              {errors.map((error, index) => (
                <div key={index} className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">
                    <strong>{error.file}:</strong> {error.message}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Files */}
      {files.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Selected Files ({files.length})</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setFiles([])}
                disabled={isUploading}
              >
                Clear All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {files.map((file) => {
                const FileIcon = getFileIcon(file.type);
                const progress = uploadProgress[file.id] || 0;
                
                return (
                  <div 
                    key={file.id} 
                    className={`p-4 border rounded-lg ${getStatusColor(file.status)}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <FileIcon className="h-5 w-5 text-gray-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Input
                              value={file.displayName}
                              onChange={(e) => updateDisplayName(file.id, e.target.value)}
                              className="font-medium text-sm bg-transparent border-none p-0 h-auto focus:ring-0"
                              disabled={isUploading}
                            />
                            {getStatusIcon(file.status)}
                          </div>
                          {!isUploading && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(file.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>{file.name}</span>
                          <span>{formatFileSize(file.size)}</span>
                          <Badge variant="secondary" className="text-xs">
                            {file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                          </Badge>
                        </div>

                        {/* Progress bar for uploading files */}
                        {file.status === 'uploading' && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{progress}% uploaded</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Version Notes */}
      {files.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Version Notes</CardTitle>
            <CardDescription>
              Describe the changes or additions in this version
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={versionNotes}
              onChange={(e) => setVersionNotes(e.target.value)}
              placeholder="Enter version notes (e.g., 'Updated presentation with latest research data, added new worksheet for practical exercises')"
              className="min-h-[80px]"
              disabled={isUploading}
            />
          </CardContent>
        </Card>
      )}

      {/* Upload Actions */}
      {files.length > 0 && (
        <div className="flex justify-end space-x-3">
          <Button 
            variant="outline" 
            onClick={() => setFiles([])}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpload}
            disabled={isUploading || files.length === 0}
            className="flex items-center space-x-2"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                <span>Upload {files.length} File{files.length !== 1 ? 's' : ''}</span>
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MaterialUpload;